/**
 * GivingLandingPage — gives to the named work, not to abstract categories.
 * Dark photographic hero, the live giving ticker beneath it, then every open
 * campaign as a photo card with its thermometer and a direct donate button.
 */
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowRight, Heart, ShieldCheck, Smartphone, CreditCard, Building2,
} from 'lucide-react';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero, FwmStatStrip } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY, FWM_CARD_ART, resolveImage } from '@/components/fwm/imagery';
import { GivingTicker, CampaignThermometer } from '@/components/fwm/GivingMomentum';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (value) => `$${Math.round(Number(value || 0)).toLocaleString()}`;

const CampaignCard = ({ campaign, index = 0 }) => {
  const art = resolveImage(campaign.hero_image_url || campaign.hero_image)
    || FWM_CARD_ART[index % FWM_CARD_ART.length];
  return (
    <article className="fwm-photo-card fwm-rise" data-testid={`giving-campaign-${campaign.slug}`}
             style={{ animationDelay: `${index * 70}ms` }}>
      <div className="fwm-photo-card-media">
        <img src={art} alt={campaign.name} loading="lazy" />
        <span className="fwm-photo-card-overline">
          {campaign.church_name || campaign.scope_label || 'Faith World Ministries'}
        </span>
      </div>
      <div className="fwm-photo-card-body">
        <h3 className="fwm-photo-card-title">{campaign.name}</h3>
        {(campaign.tagline || campaign.description) && (
          <p className="fwm-photo-card-blurb">{campaign.tagline || campaign.description}</p>
        )}
        <CampaignThermometer campaign={campaign} />
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/campaigns/${campaign.slug}/donate`} className="fwm-btn"
                data-testid={`giving-donate-${campaign.slug}`}>
            Give to this <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to={`/campaigns/${campaign.slug}`} className="fwm-btn-ghost"
                data-testid={`giving-learn-${campaign.slug}`}>
            The story
          </Link>
        </div>
      </div>
    </article>
  );
};

const GivingLandingPage = () => {
  const navigate = useNavigate();
  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['public-campaigns'],
    queryFn: () => fetch(`${API_URL}/api/campaigns`).then((r) => r.json()).catch(() => []),
    staleTime: 120000,
  });
  const live = (Array.isArray(campaigns) ? campaigns : []).filter((c) => c.status !== 'archived');
  const raised = live.reduce((sum, c) => sum + Number(c.raised_amount || c.raised || 0), 0);
  const goal = live.reduce((sum, c) => sum + Number(c.goal_amount || c.goal || 0), 0);
  const givers = live.reduce((sum, c) => sum + Number(c.donor_count || c.donors || 0), 0);
  const hero = useHero('giving', { image_url: FWM_IMAGERY.heroGiving });

  return (
    <FwmShell ctaLabel="Give now" ctaLink="/giving" testId="giving-landing" overDark>
      <FwmHero
        testId="giving-hero"
        image={hero.image_url}
        fallbackImage={hero.fallback_image_url}
        video={hero.video_url}
        mediaPriority={hero.media_priority}
        eyebrow={hero.eyebrow}
        title={hero.title}
        accent={hero.accent}
        blurb={hero.blurb}
        actions={(
          <>
            <button className="fwm-btn fwm-btn-lg" data-testid="give-now-btn"
                    onClick={() => document.getElementById('campaigns')
                      ?.scrollIntoView({ behavior: 'smooth' })}>
              Choose a work <ArrowRight className="h-4 w-4" />
            </button>
            <button className="fwm-btn-ondark" data-testid="giving-history-btn"
                    onClick={() => navigate('/login')}>
              View my giving
            </button>
          </>
        )}
      />

      <FwmStatStrip testId="giving-stats" stats={[
        [live.length, 'Open works'],
        [money(raised), 'Given so far'],
        [money(goal), 'Standing before us'],
        [givers || '—', 'Givers'],
      ]} />

      <GivingTicker />

      <section id="campaigns" className="fwm-section" data-testid="giving-campaigns">
        <div className="fwm-section-head">
          <div className="max-w-2xl">
            <h2>Where your giving goes</h2>
            <p className="mt-4 text-[color:var(--fwm-body)] leading-relaxed">
              These are the works currently before the house. Give directly to the one on
              your heart.
            </p>
          </div>
        </div>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-96 rounded-xl" />)}
          </div>
        ) : live.length === 0 ? (
          <p className="text-sm text-[color:var(--fwm-muted)]" data-testid="giving-campaigns-empty">
            No campaign is open at the moment — the general offering below is always received.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {live.map((c, i) => <CampaignCard key={c.id || c.slug} campaign={c} index={i} />)}
          </div>
        )}
      </section>

      <section className="border-t border-[color:var(--fwm-line)]">
        <div className="fwm-section grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="fwm-kicker">Tithes and undesignated giving</p>
            <h2 className="fwm-display mt-5 text-3xl sm:text-4xl tracking-tight">
              The general offering
            </h2>
            <p className="mt-5 max-w-xl text-[color:var(--fwm-body)] leading-relaxed">
              Tithes, thanksgiving, first fruits and offerings that are not tied to a campaign go
              to your local assembly and to the running of the ministry.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button className="fwm-btn-ghost fwm-btn-lg" data-testid="giving-general-offering-btn"
                      onClick={() => navigate('/churches')}>
                <Heart className="h-4 w-4" /> Give to my assembly
              </button>
              <Link to="/campaigns" className="fwm-btn-ghost fwm-btn-lg"
                    data-testid="giving-all-campaigns">
                See every campaign
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 rounded-xl border border-[color:var(--fwm-line)]
                          bg-[color:var(--fwm-card)] p-8">
            <p className="fwm-kicker">Accepted</p>
            <ul className="mt-6 space-y-4 text-sm text-[color:var(--fwm-body)]">
              {[[CreditCard, 'Card — Visa and Mastercard'],
                [Smartphone, 'EcoCash and mobile wallets'],
                [Building2, 'Bank transfer and cash at the assembly']].map(([Icon, line]) => (
                  <li key={line} className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-[color:var(--fwm-accent)] shrink-0" /> {line}
                  </li>
                ))}
            </ul>
            <p className="mt-7 flex items-center gap-2 text-xs text-[color:var(--fwm-muted)]">
              <ShieldCheck className="h-3.5 w-3.5" /> Every gift is receipted and reconciled
            </p>
          </div>
        </div>
      </section>
    </FwmShell>
  );
};

export default GivingLandingPage;
